<template>
  <div class="t-datatable">
    <div class="t-datatable__header">
      <h3><slot name="header" /></h3>
    </div>
    <div class="t-datatable__top-bar">
      <div class="t-datatable__top-bar__actions">
        <slot
          name="actions"
          :filtersData="filtersData"
          :apply="onApplyFilters"
          :update="onUpdateFilters"
        >
          &nbsp;
        </slot>
      </div>
    </div>
    <template>
      <div class="t-datatable__table-wrapper">
        <t-table-v2
          class="t-datatable__table"
          :fields="fields"
          :items="resourcesWithSkeleton"
          :order-by="orderBy"
          :card="card"
          :expanded="expanded"
          @sort="onSort"
        >
          <template #default="{ items }">
            <t-table-v2-row
              v-for="item in items"
              :key="item.id"
            >
              <t-table-v2-cell
                v-for="field in fields"
                :key="field.key"
                :content-align="field.contentAlign"
                :no-wrap="field.noWrap"
                :data-label="field.label"
              >
                <t-skeleton v-if="loading" height="2rem" />
                <slot
                  v-else
                  :name="`td(${field.key})`"
                  :item="item"
                  :value="getValue(item, field.key)"
                >
                  <span><strong>{{ getValue(item, field.key) }}</strong></span>
                </slot>
              </t-table-v2-cell>
            </t-table-v2-row>
          </template>
        </t-table-v2>
      </div>
      <div v-if="!isEmpty" class="t-datatable__footer">
        <t-select
          v-model="page.size"
          :options="pageSizesOptions"
          @change="onChangePageSize"
        />

        <span>{{ totalEntriesLabel }}</span>
        <t-pagination
          v-if="pagination.total"
          class="t-datatable__pagination"
          :current-page="pagination.current_page"
          :per-page="pagination.per_page"
          :total="pagination.total"
          @change="onChangePageNumber"
        />
      </div>
    </template>
    <div v-if="isEmpty" class="t-datatable__empty">
      <slot name="empty">
        {{ noDataLabel }}
      </slot>
    </div>
  </div>
</template>
<script>
import getValue from 'get-value';
import TPagination from '../TPagination/TPagination.vue';
import { setUrlFiltersIntoState } from '../../utils';

export default {
  name: 't-datatable',

  components: {
    TPagination,
  },

  props: {
    resources: {
      type: Array,
      required: false,
      default: () => ([]),
    },

    pagination: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    loading: {
      type: Boolean,
      required: false,
      default: false,
    },

    defaultOrderBy: {
      type: String,
      required: false,
      default: '-created_at',
    },

    defaultPageSize: {
      type: Number,
      required: false,
      default: 10,
    },

    filters: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    enabledFilters: {
      type: Array,
      required: false,
      default: () => ([]),
    },

    noDataLabel: {
      type: String,
      required: false,
      default: 'Sorry, no data available.',
    },

    fields: {
      type: Array,
      required: true,
    },

    card: {
      type: Boolean,
      required: false,
      default: false,
    },

    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },

    // TODO: replace this prop with useRoute composable
    route: {
      type: Object,
      required: true,
    },

    // TODO: replace this prop with useRouter composable
    router: {
      type: Object,
      required: true,
    },
  },

  created() {
    this.initialSetup();
    this.fetchResources();
  },

  data() {
    return {
      type: this.defaultType,
      orderBy: null,
      pageSizesOptions: [10, 20, 25, 50],
      page: {},
      filtersData: {},
    };
  },

  computed: {
    isLoaded() {
      return !!this.resources;
    },

    isEmpty() {
      return !this.loading && this.resources && this.resources.length < 1;
    },

    totalEntriesLabel() {
      return `${this.pagination.total || 0} entries found`;
    },

    resourcesWithSkeleton() {
      return this.loading
        ? Array.from({ length: this.pagination.per_page || this.defaultPageSize })
          .map((e, i) => ({ id: i + 1 }))
        : this.resources;
    },
  },

  methods: {
    initialSetup() {
      setUrlFiltersIntoState({
        filters: this.enabledFilters,
        $route: this.route,
        $set: this.$set,
        filtersData: this.filtersData,
      });

      this.orderBy = this.route.query.sort || this.defaultOrderBy;
      this.page = {
        size: this.route.query.page?.size || this.defaultPageSize,
        number: this.route.query.page?.number || 1,
      };
    },

    onUpdateFilters() {
    },

    onApplyFilters(newFilters) {
      // Reset page number to avoid possible
      // pageSize/pageNumber with empty dataset
      this.page.number = 1;
      this.filtersData = newFilters;
      this.updateUrlQuery();
    },

    onSort(direction, property) {
      const newOrderBy = direction === 'desc' ? `-${property}` : property;
      this.orderBy = newOrderBy;
      this.updateUrlQuery();
    },

    onChangePageSize(size) {
      // Reset page number to avoid possible
      // pageSize/pageNumber with empty dataset
      this.page.number = 1;
      this.page.size = size;
      this.updateUrlQuery();
    },

    onChangePageNumber(number) {
      this.page.number = number;
      this.updateUrlQuery();
    },

    async updateUrlQuery() {
      await this.router.push({
        ...this.route,
        query: {
          ...this.$route.query,
          page: this.page,
          filter: this.filtersData,
          sort: this.orderBy,
        },
      });
    },

    fetchResources() {
      this.$emit('fetch', {
        filter: this.filtersData,
        orderBy: this.orderBy,
        page: this.page,
      });
    },

    getValue,
  },

  watch: {
    'route.query': function routeQuery(query) {
      this.fetchResources({ query });
    },
  },
};
</script>
